import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  CalendarDays, DollarSign, CreditCard, Users, LogOut,
  Trash2, Edit, Plus, Search, User, Mail, Phone, MapPin, X, Bell, GraduationCap, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e26bd618-434d-4121-947e-65674a4d52da/artifacts/twhikqt6_padel%20logo%20png%202.png";

const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("admin");
  const [activeTab, setActiveTab] = useState("reservations");
  const [stats, setStats] = useState({ total_reservations: 0, today_reservations: 0, total_income: 0, online_payments: 0 });
  const [reservations, setReservations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [agenda, setAgenda] = useState({ personal_classes: [], clinic_registrations: [] });
  const [filterDate, setFilterDate] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [editPassword, setEditPassword] = useState("");
  const [editUnlocked, setEditUnlocked] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editReservation, setEditReservation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", address: "", city: "", postal_code: "", phone: "" });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [bulkDates, setBulkDates] = useState([]);
  const [bulkSlot, setBulkSlot] = useState("");
  const [bulkCourt, setBulkCourt] = useState(1);
  const [bulkPrice, setBulkPrice] = useState(600);
  const [bulkCustomer, setBulkCustomer] = useState({ name: "", email: "", address: "", city: "", postal_code: "", phone: "" });
  const [recurringDay, setRecurringDay] = useState(0);
  const [recurringSlot, setRecurringSlot] = useState("");
  const [recurringCourt, setRecurringCourt] = useState(1);
  const [recurringPrice, setRecurringPrice] = useState(600);
  const [recurringStart, setRecurringStart] = useState("");
  const [recurringEnd, setRecurringEnd] = useState("");
  const [recurringCustomer, setRecurringCustomer] = useState({ name: "", email: "", address: "", city: "", postal_code: "", phone: "" });
  const [weeklyData, setWeeklyData] = useState(null);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  });
  const [showHistory, setShowHistory] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Check role first
      const meRes = await axios.get(`${API}/auth/me`, getAuthHeader());
      const role = meRes.data.role;
      setUserRole(role);
      
      if (role === "professor") {
        setActiveTab("agenda");
        const agendaRes = await axios.get(`${API}/classes/professor-agenda${filterDate ? `?date=${filterDate}` : ""}`, getAuthHeader());
        setAgenda(agendaRes.data);
      } else {
        const [statsRes, reservationsRes, customersRes, agendaRes, weekRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, getAuthHeader()),
          axios.get(`${API}/reservations${filterDate ? `?date=${filterDate}` : showHistory ? `?show=all` : ""}`, getAuthHeader()),
          axios.get(`${API}/customers`, getAuthHeader()),
          axios.get(`${API}/classes/professor-agenda${filterDate ? `?date=${filterDate}` : ""}`, getAuthHeader()),
          axios.get(`${API}/dashboard/weekly-view?start_date=${weekStart}`, getAuthHeader()),
        ]);
        setStats(statsRes.data);
        setReservations(reservationsRes.data);
        setCustomers(customersRes.data);
        setAgenda(agendaRes.data);
        setWeeklyData(weekRes.data);
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [filterDate, navigate, weekStart, showHistory]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return; }
    fetchData();
  }, [fetchData, navigate]);

  const fetchSlots = async (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    try {
      const { data } = await axios.get(`${API}/reservations/available-slots?date=${dateStr}`);
      setSlots(data.slots || []);
    } catch { setSlots([]); }
  };

  const searchCustomer = async (email) => {
    if (email.length < 3) { setSuggestions([]); return; }
    try {
      const { data } = await axios.get(`${API}/customers/search?email=${encodeURIComponent(email)}`);
      setSuggestions(data);
      setShowSuggestions(data.length > 0);
    } catch { setSuggestions([]); }
  };

  const selectSuggestion = (c) => {
    setNewCustomer({ name: c.name || "", email: c.email || "", address: c.address || "", city: c.city || "", postal_code: c.postal_code || "", phone: c.phone || "" });
    setShowSuggestions(false);
  };

  const handleCreateManual = async () => {
    if (!selectedDate || !selectedSlot || !selectedCourt) {
      toast.error("Selecciona fecha, horario y cancha");
      return;
    }
    if (!newCustomer.name || !newCustomer.phone) {
      toast.error("Completa nombre y teléfono");
      return;
    }
    try {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
      const slot = slots.find(s => s.time === selectedSlot);
      await axios.post(`${API}/reservations/manual`, {
        date: dateStr,
        time_slot: selectedSlot,
        court: selectedCourt,
        price: slot?.price || 600,
        payment_type: "manual",
        customer: newCustomer,
      }, getAuthHeader());
      toast.success("Reserva manual creada exitosamente");
      setShowCreateModal(false);
      resetCreateForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear reserva");
    }
  };

  const requirePassword = (action) => {
    if (editUnlocked) {
      action();
    } else {
      setPendingAction(() => action);
      setEditPassword("");
      setShowPasswordDialog(true);
    }
  };

  const verifyPassword = async () => {
    try {
      await axios.post(`${API}/admin/verify-edit-password`, { password: editPassword }, getAuthHeader());
      setEditUnlocked(true);
      setShowPasswordDialog(false);
      if (pendingAction) pendingAction();
      setPendingAction(null);
    } catch {
      toast.error("Clave incorrecta");
    }
  };

  const confirmDelete = (id) => {
    requirePassword(() => {
      setDeleteId(id);
      setShowDeleteDialog(true);
    });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/reservations/${deleteId}`, getAuthHeader());
      toast.success("Reserva eliminada");
      setShowDeleteDialog(false);
      setDeleteId(null);
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar: " + (err.response?.data?.detail || ""));
    }
  };

  const handleEdit = (res) => {
    requirePassword(() => {
      setEditReservation({ ...res });
      setShowEditModal(true);
    });
  };

  const handleUpdate = async () => {
    if (!editReservation) return;
    try {
      await axios.put(`${API}/reservations/${editReservation.id}`, {
        status: editReservation.status,
        date: editReservation.date,
        time_slot: editReservation.time_slot,
        court: editReservation.court,
        price: editReservation.price,
        notes: editReservation.notes || "",
      }, getAuthHeader());
      toast.success("Reserva actualizada");
      setShowEditModal(false);
      fetchData();
    } catch (err) {
      toast.error("Error al actualizar");
    }
  };

  const resetCreateForm = () => {
    setSelectedDate(null);
    setSlots([]);
    setSelectedSlot("");
    setSelectedCourt(null);
    setNewCustomer({ name: "", email: "", address: "", city: "", postal_code: "", phone: "" });
  };

  const handleDeleteCustomer = (email) => {
    requirePassword(async () => {
      try {
        await axios.delete(`${API}/customers/${encodeURIComponent(email)}`, getAuthHeader());
        toast.success("Cliente eliminado");
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al eliminar cliente");
      }
    });
  };

  const handleDeleteClass = (classId) => {
    requirePassword(async () => {
      try {
        await axios.delete(`${API}/classes/${classId}`, getAuthHeader());
        toast.success("Clase cancelada");
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al cancelar clase");
      }
    });
  };

  const ALL_SLOTS = [
    "6:00 AM - 7:30 AM", "7:30 AM - 9:00 AM", "9:00 AM - 10:30 AM",
    "10:30 AM - 12:00 PM", "12:00 PM - 1:30 PM", "1:30 PM - 3:00 PM",
    "3:30 PM - 5:00 PM", "5:00 PM - 6:30 PM", "6:30 PM - 8:00 PM",
    "8:00 PM - 9:30 PM", "9:30 PM - 11:00 PM"
  ];
  const DAYS_ES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

  const handleBulkSubmit = async () => {
    if (bulkDates.length === 0 || !bulkSlot || !bulkCustomer.name || !bulkCustomer.phone) {
      toast.error("Selecciona fechas, horario y completa nombre y teléfono");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/reservations/bulk`, {
        dates: bulkDates, time_slot: bulkSlot, court: bulkCourt,
        price: bulkPrice, customer: bulkCustomer,
      }, getAuthHeader());
      toast.success(`${data.created} reservas creadas${data.skipped > 0 ? `, ${data.skipped} omitidas (ocupadas)` : ""}`);
      setShowBulkModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleRecurringSubmit = async () => {
    if (!recurringSlot || !recurringStart || !recurringEnd || !recurringCustomer.name || !recurringCustomer.phone) {
      toast.error("Completa todos los campos");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/reservations/recurring`, {
        day_of_week: recurringDay, time_slot: recurringSlot, court: recurringCourt,
        price: recurringPrice, start_date: recurringStart, end_date: recurringEnd,
        customer: recurringCustomer,
      }, getAuthHeader());
      toast.success(`${data.created} reservas recurrentes creadas${data.skipped > 0 ? `, ${data.skipped} omitidas` : ""}`);
      setShowRecurringModal(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleBulkDate = (dateStr) => {
    setBulkDates(prev => prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]);
  };

  const handleCleanupTestData = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/cleanup-test-data`, {}, getAuthHeader());
      toast.success(`Limpieza: ${data.customers_deleted} clientes y ${data.reservations_deleted} reservas de prueba eliminadas`);
      fetchData();
    } catch (err) { toast.error("Error en limpieza"); }
  };

  const handleSendReminders = async () => {
    try {
      const { data } = await axios.post(`${API}/reminders/send-now`, {}, getAuthHeader());
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar recordatorios");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const statusBadge = (status) => {
    const colors = {
      confirmed: "bg-green-100 text-green-700",
      pending: "bg-yellow-100 text-yellow-700",
      cancelled: "bg-red-100 text-red-700",
      rejected: "bg-red-100 text-red-700",
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || "bg-zinc-100 text-zinc-700"}`}>
        {status}
      </span>
    );
  };

  const filteredCustomers = customerSearch
    ? customers.filter(c =>
        c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.email?.toLowerCase().includes(customerSearch.toLowerCase()) ||
        c.phone?.includes(customerSearch)
      )
    : customers;

  if (loading) return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-zinc-950 border-t-transparent" />
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <Toaster position="top-center" />

      {/* Admin Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-50" data-testid="admin-header">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="PCDR" className="h-10" />
            <span className="font-semibold text-zinc-950 text-sm">
              {userRole === "professor" ? "Agenda Profesor" : "Panel Admin"}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Salir
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats - admin only */}
        {userRole === "admin" && (
        <>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4" data-testid="stats-grid">
          {[
            { label: "Reservas Hoy", value: stats.today_reservations, icon: CalendarDays, color: "text-blue-600" },
            { label: "Total Reservas", value: stats.total_reservations, icon: Users, color: "text-green-600" },
            { label: "Clases Hoy", value: stats.today_classes, icon: GraduationCap, color: "text-purple-600" },
            { label: "Pagos Online", value: stats.online_payments, icon: CreditCard, color: "text-amber-600" },
          ].map((stat, i) => (
            <Card key={i} className="border border-zinc-200 shadow-none" data-testid={`stat-${i}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-zinc-100">
                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{stat.label}</p>
                    <p className="text-lg font-bold text-zinc-950">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="border border-zinc-200 shadow-none"><CardContent className="p-3 text-center"><p className="text-[10px] text-zinc-500 uppercase">Total Clases</p><p className="text-lg font-bold">{stats.total_classes || 0}</p></CardContent></Card>
          <Card className="border border-zinc-200 shadow-none"><CardContent className="p-3 text-center"><p className="text-[10px] text-zinc-500 uppercase">Clínicas Inscritos</p><p className="text-lg font-bold">{stats.total_clinic || 0}</p></CardContent></Card>
          <Card className="border border-zinc-200 shadow-none"><CardContent className="p-3 text-center"><p className="text-[10px] text-zinc-500 uppercase">Ingresos Clases</p><p className="text-lg font-bold">${(stats.class_income || 0).toLocaleString()}</p></CardContent></Card>
        </div>
        </>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-zinc-200">
          {userRole === "admin" && (
          <>
          <button
            onClick={() => setActiveTab("reservations")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "reservations"
                ? "border-zinc-950 text-zinc-950"
                : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
            data-testid="tab-reservations"
          >
            <CalendarDays className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Reservaciones
          </button>
          <button
            onClick={() => setActiveTab("weekly")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "weekly"
                ? "border-zinc-950 text-zinc-950"
                : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
            data-testid="tab-weekly"
          >
            <Clock className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Calendario
          </button>
          <button
            onClick={() => setActiveTab("customers")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "customers"
                ? "border-zinc-950 text-zinc-950"
                : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
            data-testid="tab-customers"
          >
            <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Clientes ({customers.length})
          </button>
          </>
          )}
          <button
            onClick={() => setActiveTab("agenda")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "agenda"
                ? "border-zinc-950 text-zinc-950"
                : "border-transparent text-zinc-500 hover:text-zinc-700"
            }`}
            data-testid="tab-agenda"
          >
            <GraduationCap className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Agenda Profesor
          </button>
        </div>

        {/* RESERVATIONS TAB */}
        {activeTab === "reservations" && (
          <>
            {/* Actions bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-zinc-950">Reservaciones</h2>
                <button onClick={() => setShowHistory(!showHistory)} className={`text-xs px-2 py-1 rounded transition-colors ${showHistory ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`} data-testid="toggle-history">
                  {showHistory ? "Mostrando todo" : "Solo próximas"}
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-zinc-400" />
                  <Input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="w-44 border-zinc-300 text-sm"
                    data-testid="filter-date"
                  />
                  {filterDate && (
                    <Button variant="ghost" size="sm" onClick={() => setFilterDate("")} data-testid="clear-filter">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={handleSendReminders}
                  className="border-zinc-300"
                  data-testid="send-reminders-btn"
                >
                  <Bell className="w-4 h-4 mr-1" /> Recordatorios
                </Button>
                <Button
                  onClick={() => { resetCreateForm(); setShowCreateModal(true); }}
                  className="bg-zinc-950 text-white hover:bg-zinc-800"
                  data-testid="create-reservation-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Nueva
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setBulkDates([]); setBulkSlot(""); setBulkCustomer({ name: "", email: "", address: "", city: "", postal_code: "", phone: "" }); setShowBulkModal(true); }}
                  className="border-zinc-300"
                  data-testid="bulk-reservation-btn"
                >
                  <CalendarDays className="w-4 h-4 mr-1" /> Múltiple
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setRecurringSlot(""); setRecurringCustomer({ name: "", email: "", address: "", city: "", postal_code: "", phone: "" }); setShowRecurringModal(true); }}
                  className="border-zinc-300"
                  data-testid="recurring-reservation-btn"
                >
                  <Clock className="w-4 h-4 mr-1" /> Recurrente
                </Button>
              </div>
            </div>

            {/* Reservations Table */}
            <Card className="border border-zinc-200 shadow-none overflow-hidden" data-testid="reservations-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Fecha</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Horario</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Cancha</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Cliente</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Precio</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Pago</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Estado</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Notas</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservations.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-12 text-center text-zinc-400">
                          No hay reservaciones
                        </td>
                      </tr>
                    ) : (
                      reservations.map((res) => (
                        <tr key={res.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`reservation-row-${res.id}`}>
                          <td className="px-4 py-3 font-medium">{res.date}</td>
                          <td className="px-4 py-3">{res.time_slot}</td>
                          <td className="px-4 py-3">Cancha {res.court}</td>
                          <td className="px-4 py-3">
                            <div>{res.customer?.name}</div>
                            <div className="text-xs text-zinc-400">{res.customer?.email}</div>
                          </td>
                          <td className="px-4 py-3">${res.price} MXN</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-medium ${res.payment_type === "online" ? "text-blue-600" : "text-zinc-600"}`}>
                              {res.payment_type === "online" ? "En línea" : "Manual"}
                            </span>
                          </td>
                          <td className="px-4 py-3">{statusBadge(res.status)}</td>
                          <td className="px-4 py-3 text-xs text-zinc-500 max-w-[120px] truncate">{res.notes || "-"}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(res)} data-testid={`edit-btn-${res.id}`}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => confirmDelete(res.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-btn-${res.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}


        {/* WEEKLY CALENDAR TAB */}
        {activeTab === "weekly" && weeklyData && (
          <>
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" onClick={() => {
                const d = new Date(weekStart); d.setDate(d.getDate() - 7);
                setWeekStart(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
              }} data-testid="week-prev">&larr; Anterior</Button>
              <h3 className="font-semibold text-zinc-950 text-sm">
                Semana del {new Date(weekStart + "T12:00:00").toLocaleDateString("es-MX", {day:"numeric", month:"short"})} al {new Date(new Date(weekStart + "T12:00:00").getTime() + 6*86400000).toLocaleDateString("es-MX", {day:"numeric", month:"short", year:"numeric"})}
              </h3>
              <Button variant="outline" size="sm" onClick={() => {
                const d = new Date(weekStart); d.setDate(d.getDate() + 7);
                setWeekStart(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
              }} data-testid="week-next">Siguiente &rarr;</Button>
            </div>
            <Card className="border border-zinc-200 shadow-none overflow-hidden" data-testid="weekly-grid">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="px-2 py-2 text-left font-medium text-zinc-600 sticky left-0 bg-zinc-50 min-w-[100px]">Horario</th>
                      {(weeklyData.dates || []).map(date => {
                        const wd = weeklyData.weekly[date];
                        const dayNum = date.split("-")[2];
                        return <th key={date} className={`px-2 py-2 text-center font-medium min-w-[90px] ${wd?.closed ? "text-red-400" : "text-zinc-700"}`}>{wd?.day_name || "Dom"} {dayNum}</th>;
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {(["6:00 AM - 7:30 AM","7:30 AM - 9:00 AM","9:00 AM - 10:30 AM","10:30 AM - 12:00 PM","12:00 PM - 1:30 PM","1:30 PM - 3:00 PM","3:30 PM - 5:00 PM","5:00 PM - 6:30 PM","6:30 PM - 8:00 PM","8:00 PM - 9:30 PM","9:30 PM - 11:00 PM"]).map(slot => (
                      <tr key={slot} className="border-b border-zinc-100">
                        <td className="px-2 py-1.5 font-medium text-zinc-700 sticky left-0 bg-white border-r border-zinc-100 whitespace-nowrap">{slot}</td>
                        {(weeklyData.dates || []).map(date => {
                          const wd = weeklyData.weekly[date];
                          if (wd?.closed) return <td key={date} className="px-1 py-1.5 text-center bg-red-50 text-red-300">Cerrado</td>;
                          const s = wd?.slots?.[slot];
                          if (!s) return <td key={date} className="px-1 py-1.5 bg-zinc-50">-</td>;
                          const colors = {
                            available: "bg-green-50 text-green-700",
                            partial: "bg-yellow-50 text-yellow-700",
                            full: "bg-red-50 text-red-600",
                            class: "bg-blue-50 text-blue-700",
                            clinic: "bg-purple-50 text-purple-700",
                          };
                          return (
                            <td key={date} className={`px-1 py-1.5 text-center ${colors[s.status] || "bg-zinc-50"}`}>
                              <span className="block truncate max-w-[85px] mx-auto">{s.label || (s.status === "available" ? "Libre" : "")}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <div className="flex items-center gap-4 mt-3 text-xs">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 inline-block"></span> Libre</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-200 inline-block"></span> 1 Cancha</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 inline-block"></span> Completo</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-200 inline-block"></span> Clase</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-200 inline-block"></span> Clínica</span>
            </div>
          </>
        )}

        {/* CUSTOMERS TAB */}
        {activeTab === "customers" && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-semibold text-zinc-950">Clientes Registrados</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => requirePassword(handleCleanupTestData)} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="cleanup-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Limpiar Pruebas
                </Button>
                <Search className="w-4 h-4 text-zinc-400" />
                <Input
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Buscar por nombre, email o teléfono..."
                  className="w-72 border-zinc-300 text-sm"
                  data-testid="customer-search-input"
                />
                {customerSearch && (
                  <Button variant="ghost" size="sm" onClick={() => setCustomerSearch("")}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            <Card className="border border-zinc-200 shadow-none overflow-hidden" data-testid="customers-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Nombre</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Email</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Teléfono</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Dirección</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Ciudad</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">C.P.</th>
                      <th className="px-4 py-3 text-left font-medium text-zinc-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-zinc-400">
                          {customerSearch ? "No se encontraron clientes" : "No hay clientes registrados"}
                        </td>
                      </tr>
                    ) : (
                      filteredCustomers.map((c, i) => (
                        <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`customer-row-${i}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-zinc-500" />
                              </div>
                              <span className="font-medium">{c.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-zinc-600">
                              <Mail className="w-3.5 h-3.5" /> {c.email}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-zinc-600">
                              <Phone className="w-3.5 h-3.5" /> {c.phone}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-zinc-600">{c.address || "-"}</td>
                          <td className="px-4 py-3 text-zinc-600">{c.city || "-"}</td>
                          <td className="px-4 py-3 text-zinc-600">{c.postal_code || "-"}</td>
                          <td className="px-4 py-3">
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteCustomer(c.email)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-customer-${i}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {/* AGENDA TAB */}
        {activeTab === "agenda" && (
          <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-lg sm:text-xl font-semibold text-zinc-950">Agenda - Prof. Diego González</h2>
              <div className="flex items-center gap-2">
                <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-40 border-zinc-300 text-sm" data-testid="agenda-filter-date" />
                {filterDate && <Button variant="ghost" size="sm" onClick={() => setFilterDate("")}><X className="w-4 h-4" /></Button>}
              </div>
            </div>

            {/* Personal Classes - Card layout */}
            <h3 className="font-medium text-zinc-800 mb-3 text-sm uppercase tracking-wider">Clases Personalizadas</h3>
            {(agenda.personal_classes || []).length === 0 ? (
              <Card className="border border-zinc-200 shadow-none mb-8"><CardContent className="p-8 text-center text-zinc-400 text-sm">No hay clases personalizadas</CardContent></Card>
            ) : (
              <div className="space-y-3 mb-8">
                {(agenda.personal_classes || []).map((cls) => (
                  <Card key={cls.id} className="border border-zinc-200 shadow-none" data-testid={`class-card-${cls.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="font-bold text-zinc-950 text-sm">{cls.date}</span>
                            <span className="text-xs bg-zinc-100 px-2 py-0.5 rounded">{cls.time_slot}</span>
                            <span className="text-xs bg-zinc-100 px-2 py-0.5 rounded">Cancha {cls.court}</span>
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{cls.status}</span>
                          </div>
                          <div className="space-y-1">
                            {(cls.participants || []).map((p, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <User className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                                <span className="font-medium">{p.name}</span>
                                <span className="text-zinc-400">{p.phone}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-zinc-500 mt-2">${cls.price_per_person} x {cls.num_participants} = <strong>${cls.total_price} MXN</strong></p>
                        </div>
                        {userRole === "admin" && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteClass(cls.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0" data-testid={`delete-class-${cls.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Clinic Registrations - Card layout */}
            <h3 className="font-medium text-zinc-800 mb-3 text-sm uppercase tracking-wider">Clínicas de Niños</h3>
            {(agenda.clinic_registrations || []).length === 0 ? (
              <Card className="border border-zinc-200 shadow-none"><CardContent className="p-8 text-center text-zinc-400 text-sm">No hay inscripciones</CardContent></Card>
            ) : (
              <div className="space-y-3">
                {(agenda.clinic_registrations || []).map((reg) => (
                  <Card key={reg.id} className="border border-zinc-200 shadow-none" data-testid={`clinic-card-${reg.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="font-bold text-zinc-950 text-sm">{reg.date}</span>
                            <span className="text-xs bg-zinc-100 px-2 py-0.5 rounded">{reg.time_slot}</span>
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">{reg.status}</span>
                          </div>
                          <p className="text-sm"><strong>{reg.child_name}</strong> - {reg.child_age} años</p>
                          <p className="text-xs text-zinc-500 mt-1">Padre: {reg.parent_name} | Tel: {reg.parent_phone}</p>
                        </div>
                        {userRole === "admin" && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteClass(reg.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0" data-testid={`delete-clinic-${reg.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Reserva</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas eliminar esta reserva? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowDeleteDialog(false); setDeleteId(null); }} data-testid="delete-cancel-btn">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Manual Reservation Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Reserva Manual (Cliente Frecuente)</DialogTitle>
            <DialogDescription>Crea una reserva sin requerir pago en línea.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Calendar in modal */}
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => { setSelectedDate(d); if (d) fetchSlots(d); setSelectedSlot(""); setSelectedCourt(null); }}
                disabled={[{ dayOfWeek: [0] }, { before: new Date() }]}
                fromDate={new Date()}
                toDate={new Date(2030, 11, 31)}
                className="rounded-md border"
                data-testid="modal-calendar"
              />
            </div>

            {/* Slots */}
            {selectedDate && slots.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Horarios disponibles:</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {slots.filter(s => !s.fully_booked).map((slot) => (
                    <button
                      key={slot.time}
                      onClick={() => { setSelectedSlot(slot.time); setSelectedCourt(null); }}
                      className={`p-2 border rounded text-xs text-center transition-all ${
                        selectedSlot === slot.time ? "border-2 border-zinc-950 bg-zinc-50 font-semibold" : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      data-testid={`modal-slot-${slot.time.replace(/[:\s]/g, "-")}`}
                    >
                      {slot.time}
                      <br /><span className="text-zinc-500">${slot.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Court */}
            {selectedSlot && (
              <div>
                <p className="text-sm font-medium mb-2">Cancha:</p>
                <div className="flex gap-3">
                  {slots.find(s => s.time === selectedSlot)?.available_courts.map((court) => (
                    <button
                      key={court}
                      onClick={() => setSelectedCourt(court)}
                      className={`flex-1 p-2 border rounded text-sm ${
                        selectedCourt === court ? "border-2 border-zinc-950 bg-zinc-50 font-semibold" : "border-zinc-200 hover:border-zinc-400"
                      }`}
                      data-testid={`modal-court-${court}`}
                    >
                      Cancha {court}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Customer Form */}
            <div>
              <p className="text-sm font-medium mb-3">Datos del Cliente:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><User className="w-3 h-3" /> Nombre *</label>
                  <Input
                    data-testid="modal-customer-name"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    placeholder="Nombre"
                    className="text-sm"
                  />
                </div>
                <div className="relative">
                  <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><Mail className="w-3 h-3" /> Email</label>
                  <Input
                    data-testid="modal-customer-email"
                    value={newCustomer.email}
                    onChange={(e) => { setNewCustomer({ ...newCustomer, email: e.target.value }); searchCustomer(e.target.value); }}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="correo@ejemplo.com"
                    className="text-sm"
                  />
                  {showSuggestions && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-32 overflow-y-auto">
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => selectSuggestion(s)}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 border-b border-zinc-100"
                        >
                          <span className="font-medium">{s.name}</span> - {s.email}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><Phone className="w-3 h-3" /> Teléfono *</label>
                  <Input
                    data-testid="modal-customer-phone"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    placeholder="Teléfono"
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" /> Dirección</label>
                  <Input
                    data-testid="modal-customer-address"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                    placeholder="Dirección"
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Ciudad</label>
                  <Input
                    data-testid="modal-customer-city"
                    value={newCustomer.city}
                    onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                    placeholder="Ciudad"
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Código Postal</label>
                  <Input
                    data-testid="modal-customer-postal"
                    value={newCustomer.postal_code}
                    onChange={(e) => setNewCustomer({ ...newCustomer, postal_code: e.target.value })}
                    placeholder="C.P."
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} data-testid="modal-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={handleCreateManual}
              className="bg-zinc-950 text-white hover:bg-zinc-800"
              data-testid="modal-create-btn"
            >
              Crear Reserva Manual
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Reserva</DialogTitle>
            <DialogDescription>Modifica los detalles de la reserva.</DialogDescription>
          </DialogHeader>
          {editReservation && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Fecha</label>
                <Input
                  data-testid="edit-date"
                  type="date"
                  value={editReservation.date}
                  onChange={(e) => setEditReservation({ ...editReservation, date: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Horario</label>
                <Input
                  data-testid="edit-time"
                  value={editReservation.time_slot}
                  onChange={(e) => setEditReservation({ ...editReservation, time_slot: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Cancha</label>
                <Input
                  data-testid="edit-court"
                  type="number"
                  min={1}
                  max={2}
                  value={editReservation.court}
                  onChange={(e) => setEditReservation({ ...editReservation, court: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Precio</label>
                <Input
                  data-testid="edit-price"
                  type="number"
                  value={editReservation.price}
                  onChange={(e) => setEditReservation({ ...editReservation, price: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Estado</label>
                <select
                  data-testid="edit-status"
                  value={editReservation.status}
                  onChange={(e) => setEditReservation({ ...editReservation, status: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="pending">Pendiente</option>
                  <option value="confirmed">Confirmada</option>
                  <option value="cancelled">Cancelada</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Notas</label>
                <Input
                  data-testid="edit-notes"
                  value={editReservation.notes || ""}
                  onChange={(e) => setEditReservation({ ...editReservation, notes: e.target.value })}
                  placeholder="Notas opcionales..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} data-testid="edit-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={handleUpdate} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="edit-save-btn">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Verification Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Clave de Edición</DialogTitle>
            <DialogDescription>Ingresa la clave para editar o eliminar reservas.</DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            value={editPassword}
            onChange={(e) => setEditPassword(e.target.value)}
            placeholder="Ingresa la clave"
            onKeyDown={(e) => e.key === "Enter" && verifyPassword()}
            data-testid="edit-password-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Cancelar</Button>
            <Button onClick={verifyPassword} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="verify-password-btn">
              Verificar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reservation Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reserva Múltiple</DialogTitle>
            <DialogDescription>Selecciona varias fechas para reservar el mismo horario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center">
              <Calendar
                mode="multiple"
                selected={bulkDates.map(d => new Date(d + "T12:00:00"))}
                onSelect={(dates) => setBulkDates((dates || []).map(d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`))}
                disabled={[{ dayOfWeek: [0] }, { before: new Date() }]}
                fromDate={new Date()}
                toDate={new Date(2030, 11, 31)}
                className="rounded-md border"
                data-testid="bulk-calendar"
              />
            </div>
            {bulkDates.length > 0 && <p className="text-sm text-zinc-600 text-center">{bulkDates.length} fecha(s) seleccionada(s)</p>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Horario *</label>
                <select value={bulkSlot} onChange={(e) => setBulkSlot(e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm" data-testid="bulk-slot">
                  <option value="">Seleccionar...</option>
                  {ALL_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Cancha</label>
                <select value={bulkCourt} onChange={(e) => setBulkCourt(parseInt(e.target.value))} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm">
                  <option value={1}>Cancha 1</option>
                  <option value={2}>Cancha 2</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Precio por reserva</label>
              <Input type="number" value={bulkPrice} onChange={(e) => setBulkPrice(parseFloat(e.target.value))} className="text-sm" />
            </div>
            <Separator />
            <p className="text-sm font-medium">Datos del Cliente:</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Nombre *</label>
                <Input value={bulkCustomer.name} onChange={(e) => setBulkCustomer({...bulkCustomer, name: e.target.value})} className="text-sm" data-testid="bulk-name" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Teléfono *</label>
                <Input value={bulkCustomer.phone} onChange={(e) => setBulkCustomer({...bulkCustomer, phone: e.target.value})} className="text-sm" data-testid="bulk-phone" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Email</label>
                <Input value={bulkCustomer.email} onChange={(e) => setBulkCustomer({...bulkCustomer, email: e.target.value})} className="text-sm" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkModal(false)}>Cancelar</Button>
            <Button onClick={handleBulkSubmit} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="bulk-submit-btn">
              Crear {bulkDates.length} Reserva(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring Reservation Modal */}
      <Dialog open={showRecurringModal} onOpenChange={setShowRecurringModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reserva Recurrente</DialogTitle>
            <DialogDescription>Reserva cada semana el mismo día y horario hasta la fecha que indiques.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Día de la semana *</label>
              <select value={recurringDay} onChange={(e) => setRecurringDay(parseInt(e.target.value))} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm" data-testid="recurring-day">
                {DAYS_ES.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Horario *</label>
                <select value={recurringSlot} onChange={(e) => setRecurringSlot(e.target.value)} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm" data-testid="recurring-slot">
                  <option value="">Seleccionar...</option>
                  {ALL_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Cancha</label>
                <select value={recurringCourt} onChange={(e) => setRecurringCourt(parseInt(e.target.value))} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm">
                  <option value={1}>Cancha 1</option>
                  <option value={2}>Cancha 2</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Desde *</label>
                <Input type="date" value={recurringStart} onChange={(e) => setRecurringStart(e.target.value)} className="text-sm" data-testid="recurring-start" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Hasta *</label>
                <Input type="date" value={recurringEnd} onChange={(e) => setRecurringEnd(e.target.value)} className="text-sm" data-testid="recurring-end" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Precio por reserva</label>
              <Input type="number" value={recurringPrice} onChange={(e) => setRecurringPrice(parseFloat(e.target.value))} className="text-sm" />
            </div>
            <Separator />
            <p className="text-sm font-medium">Datos del Cliente:</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Nombre *</label>
                <Input value={recurringCustomer.name} onChange={(e) => setRecurringCustomer({...recurringCustomer, name: e.target.value})} className="text-sm" data-testid="recurring-name" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Teléfono *</label>
                <Input value={recurringCustomer.phone} onChange={(e) => setRecurringCustomer({...recurringCustomer, phone: e.target.value})} className="text-sm" data-testid="recurring-phone" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Email</label>
                <Input value={recurringCustomer.email} onChange={(e) => setRecurringCustomer({...recurringCustomer, email: e.target.value})} className="text-sm" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecurringModal(false)}>Cancelar</Button>
            <Button onClick={handleRecurringSubmit} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="recurring-submit-btn">
              Crear Reserva Recurrente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
